/**
 * Formats a raw bedrooms range (e.g. "studio - 3") into listed copy
 * like "Studio, 1, 2, & 3". Expands min–max into every option between
 * the endpoints. Omits "bedroom"/"bed"/"beds" — callers already hardcode
 * surrounding words like "Apartments" or "Layouts".
 *
 * Examples:
 *   "studio - 3" → "Studio, 1, 2, & 3"
 *   "1 - 2"      → "1 & 2"
 *   "2"          → "2"
 *   "studio"     → "Studio"
 *   "2 - 2"      → "2"
 */
export function formatBedroomsLabel(raw: string | null | undefined): string {
  const options = expandBedroomOptions(raw);
  if (!options.length) return '';
  return joinWithAmpersand(options);
}

function expandBedroomOptions(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const trimmed = stripBedWords(raw).trim();
  if (!trimmed) return [];

  const parts = trimmed.split(/\s*-\s*/).map((p) => stripBedWords(p).trim()).filter(Boolean);
  if (!parts.length) return [];

  const parsed = parts.map(parseBedroomPart);
  // Non-numeric / non-studio copy (custom editor text) — keep as-is, title-cased.
  if (parsed.some((n) => n === null)) {
    const formatted = parts.map((p) => {
      const lower = p.toLowerCase();
      if (lower === 'studio' || lower === 'std') return 'Studio';
      return p.replace(/\b\w/g, (c) => c.toUpperCase());
    });
    // Collapse identical endpoints (e.g. "2 - 2").
    if (formatted.length > 1 && formatted.every((p) => p === formatted[0])) {
      return [formatted[0]];
    }
    return formatted;
  }

  const nums = parsed as number[];
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const options: string[] = [];
  for (let n = min; n <= max; n++) {
    options.push(n === 0 ? 'Studio' : String(n));
  }
  return options;
}

function stripBedWords(value: string): string {
  return value.replace(/\b(bedrooms?|beds?)\b/gi, ' ').replace(/\s+/g, ' ');
}

function parseBedroomPart(part: string): number | null {
  const lower = part.trim().toLowerCase();
  if (lower === 'studio' || lower === 'std') return 0;
  const match = lower.match(/^(\d+)/);
  if (!match) return null;
  const num = parseInt(match[1], 10);
  if (isNaN(num) || num < 0) return null;
  return num;
}

function joinWithAmpersand(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} & ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, & ${items[items.length - 1]}`;
}
